import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Chip, CircularProgress } from "@mui/material";

import {
  SettingsSectionWrapper1,
  SettingsSectionWrapper2,
} from "../../pageComponents/settings/style";
import {
  PlanDescription,
  PlanDetailWrapper,
  PlanDetailsWrapper,
  PlanName,
  PlanPrice,
  Wrapper,
} from "../planDetailsSection/style";

import {
  activeSubscriptionSelector,
  isLoadingSelector,
  paymentLogsListSelector,
  paymentLogsPaginationParamsSelector,
} from "../../store/payment/selector";
import { PaymentSagaActions } from "../../store/payment/sagas";
import { PaymentReducerAction } from "../../store/payment/slice";
import { paymentLogsListDataType } from "../../store/payment/types";
import {AdDrawerStyledButton} from "../commonComponents/adDetails/style";
import {NavigatePrevIcon} from "../../assets/Icons";
import {useNavigate} from "react-router-dom";

const PaymentLogs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activePlan = useSelector(activeSubscriptionSelector);
  const paymentLogsList = useSelector(paymentLogsListSelector);
  const paginationParams = useSelector(paymentLogsPaginationParamsSelector);
  const isLoading = useSelector(isLoadingSelector);
  const loaderRef = useRef<HTMLDivElement | null>(null);

  // Initial Fetch
  useEffect(() => {
    dispatch(PaymentSagaActions.getPaymentLogsList(paginationParams));
  }, [dispatch]);

  // Infinite Scroll Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (
          first.isIntersecting &&
          !isLoading &&
          paymentLogsList.data.length < paymentLogsList.total
        ) {
          dispatch(
            PaymentReducerAction.setPaymentLogsPaginationParams({
              skip: paymentLogsList.data.length,
              limit: paginationParams.limit,
            })
          );
          dispatch(PaymentSagaActions.getPaymentLogsList(paginationParams));
        }
      },
      { threshold: 1 }
    );

    const currentElement = loaderRef.current;
    if (currentElement) observer.observe(currentElement);

    return () => {
      if (currentElement) observer.unobserve(currentElement);
    };
  }, [dispatch, isLoading, paginationParams]);

  function capitalizeString(input: string): string {
    return input
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  return (
    <SettingsSectionWrapper1>
      <AdDrawerStyledButton onClick={() => navigate(-1)}>
        <NavigatePrevIcon />
        Back
      </AdDrawerStyledButton>
      {isLoading && paymentLogsList.data.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "40vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (<>
        {paymentLogsList?.data && paymentLogsList?.data?.length > 0 ? (
        <SettingsSectionWrapper2>
          {paymentLogsList.data.map((payment: paymentLogsListDataType) => (
            <Wrapper key={payment._id}>
              <PlanDetailsWrapper>
                <PlanDetailWrapper>
                  {payment?.status && (
                    <Chip
                      label={capitalizeString(payment?.status)}
                      sx={{
                        padding: "3px 7px",
                        borderRadius: "20px",
                        background: "#FAFAFA",
                        border:
                          payment?.status === "CANCEl_PLAN"
                            ? "1px solid red"
                            : payment?.status === "PURCHSE_PLAN"
                            ? "1px solid green"
                            : "1px solid #1976d2",
                        color:
                          payment?.status === "CANCEl_PLAN"
                            ? "red"
                            : payment?.status === "PURCHSE_PLAN"
                            ? "green"
                            : "#1976d2",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    />
                  )}
                  <PlanName>{payment.planDetails?.name}</PlanName>
                  {/* Price Info */}
                  <PlanDescription>
                    Plan purchased on{" "}
                    {new Date(payment.createdAt).toLocaleDateString() ?? ""}.
                  </PlanDescription>
                  <PlanPrice weight={400} size="0.875rem">
                    Charged amount:{" "}
                    <PlanPrice weight={700} size="1rem">
                      $
                      {payment.paymentDetailsData?.amount
                        ? payment.paymentDetailsData.amount / 100
                        : 0}
                    </PlanPrice>
                  </PlanPrice>
                </PlanDetailWrapper>
              </PlanDetailsWrapper>
            </Wrapper>
          ))}

          {/* Infinite Scroll Trigger */}
          <Box
            ref={loaderRef}
            sx={{ height: "50px", textAlign: "center", mt: 2 }}
          >
            {isLoading && <CircularProgress size={24} />}
          </Box>
        </SettingsSectionWrapper2>
            ):
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "20vh",
              }}
            >
                <PlanName>No payment logs found</PlanName>
            </Box>
            }
      </>)}
    </SettingsSectionWrapper1>
  );
};

export default PaymentLogs;
